// ============================================================================
// galaxy-core.ts — tokens, types, motion constants for Nova Galaxy + Passport.
// Source of truth: "Nova Galaxy + Passport" design doc, board 17.
// ============================================================================

export const GalaxyColor = {
  deepSpace: "#0A0E1A", // canvas
  space1: "#161719", // raised surfaces
  // POL-17 — the old value `#F4EFE6` had a warm-beige undertone that
  // read as stale (not editorial) once we saw it at full-screen scale
  // on the passport surfaces.  Swapped to a cool warm-white
  // (`#F5F3EF`) so hero copy, ring tracks, and dividers read as
  // paper, never cream.  Name kept as `ivory` so every consumer in
  // `passport/*` continues to work without a rename.
  ivory: "#F5F3EF", // type, ring tracks
  orange: "#D4541F", // reachable, Nova moments
  // PASS-2 / BRAND-1 — Kapton Gold, Passport's section accent
  // (docs/orbit-palette.md).  Frozen at the dark-surface readable
  // tone (43 60% 46%) because every Passport surface is always
  // deep-space regardless of app theme — same "frozen dark tones"
  // pattern the `.new-shell` chrome accents use.  UI accents
  // (eyebrows, CTAs, progress fills, unread dots) wear THIS;
  // `orange` stays only as ink inside planet/stamp ART (ember skin,
  // bookings ring ink — Character Bible exemption).
  gold: "#BC942F", // Kapton Gold — Passport UI accent
  blue: "#A8C8E8", // trails, craft ink
  mustard: "#D4A02C", // revenue ink
  coral: "#E8A98D", // audience ink
  teal: "#2D6E70", // Nova body
  tealHi: "#5FA3A5", // teal lifted for dark bg
} as const;

export type SubGoalType = "bookings" | "craft" | "revenue" | "audience" | "ops";

export const SUB_GOAL_INK: Record<SubGoalType, string> = {
  bookings: GalaxyColor.orange,
  craft: GalaxyColor.blue,
  revenue: GalaxyColor.mustard,
  audience: GalaxyColor.coral,
  ops: GalaxyColor.tealHi,
};

export type PlanetSkin = "ember" | "glacier" | "brass" | "rose" | "tide" | "slate";

/** radial gradient stops: highlight → base → shadow */
export const PLANET_SKIN: Record<PlanetSkin, [string, string, string]> = {
  ember: ["#E8895B", "#D4541F", "#7A2E10"],
  glacier: ["#CFE2F4", "#A8C8E8", "#4A6B8C"],
  brass: ["#EAC161", "#D4A02C", "#7A5A14"],
  rose: ["#F2C4AB", "#E8A98D", "#8C5A44"],
  tide: ["#6FA3A5", "#2D6E70", "#143A3C"],
  slate: ["#6A7284", "#3A4152", "#1B2030"],
};

export type PlanetState = "locked" | "active" | "reachable" | "done";

export interface SubGoal {
  id: string;
  title: string;
  type: SubGoalType;
  /** 0..1 — 1 = ring cleared */
  progress: number;
}

export interface Goal {
  id: string;
  title: string;
  /** destination world, e.g. "Dunes" */
  worldName: string;
  skin: PlanetSkin;
  state: PlanetState;
  /** Ring order = list order, innermost first. 0–5, hard max 5. */
  subGoals: SubGoal[];
  stampedAt?: string;
}

export const isReachable = (g: Goal) =>
  g.subGoals.length > 0 && g.subGoals.every((s) => s.progress >= 1);

// ── Motion (board 17) — framer-motion transition presets ────────────────────
/** Standard curve — everything except celebration + stamp. */
export const EASE_STANDARD = [0.4, 0, 0.2, 1] as const;
/** Bounce — celebration + stamp landing ONLY. */
export const EASE_BOUNCE = [0.34, 1.56, 0.64, 1] as const;

export const MOTION = {
  ringDraw: 0.3, // arc draw per completion beat
  unlockHalo: 0.8, // one pulse, then never again
  cameraPullOut: 0.9,
  transit: 3.2, // constant, regardless of distance
  stampLanding: 0.9, // 2.4× → 0.94 → 1.0
  novaFloat: 3.0, // ±10px Y
  novaBlink: 4.0, // universal track, per-instance delay
  starTwinkle: 5.0, // opacity 0.12 → 0.55
} as const;
